import { useState, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Check, ChevronDown, Search, SlidersHorizontal, Upload, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import Link from '@/routing/Link'
import { AudienceSummary } from '@/lib/api'
import { AudienceSharingGlyph } from '@/components/audiences/AudienceSharingGlyph'
import { TooltipProvider } from '@/components/ui/tooltip'

interface AudienceSelectorProps {
  audiences: Array<AudienceSummary>
  selectedId: string | null
  onSelect: (id: string | null) => void
  isLoading: boolean
  /**
   * Opt-in audience-LIST filter affordance shown inside the popover (right of the
   * search field). When provided, clicking it closes the popover and invokes the
   * handler — the caller owns the filter UI (e.g. the shared AudienceFilterModal).
   * Existing consumers that omit this keep the original search-only behaviour.
   */
  onOpenFilters?: () => void
  /** Active list-filter count, rendered as a small badge on the Filters affordance. */
  activeFilterCount?: number
}

export function AudienceSelector(props: AudienceSelectorProps) {
  const { audiences, selectedId, onSelect, isLoading, onOpenFilters, activeFilterCount = 0 } = props
  const { t } = useTranslation('app')

  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return audiences
    const q = searchQuery.toLowerCase()
    return audiences.filter((a) =>
      a.name.toLowerCase().includes(q) ||
      (a.audienceType ?? '').toLowerCase().includes(q) ||
      (a.aiDescription ?? '').toLowerCase().includes(q)
    )
  }, [audiences, searchQuery])

  const selectedAudience = useMemo(
    () => audiences.find((a) => a.id === selectedId) ?? null,
    [audiences, selectedId],
  )

  useEffect(() => {
    if (open) {
      setSearchQuery('')
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  if (isLoading) {
    return (
      <Skeleton className="h-11 w-full rounded-lg" />
    )
  }

  if (audiences.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        <Upload className="h-4 w-4 shrink-0" />
        <span>
          Upload your first audience to start chatting.{' '}
          <Link to="/audiences/new" className="font-medium text-primary underline underline-offset-2 hover:text-primary/80">
            {t('audiences.createCta')}
          </Link>
        </span>
      </div>
    )
  }

  return (
    <TooltipProvider delayDuration={300}>
    <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid="compose-audience-trigger"
            className={cn(
              'flex h-11 w-full items-center justify-between rounded-lg border border-input bg-background px-4 py-2 text-sm transition-colors',
              'hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring',
              !selectedAudience && 'text-muted-foreground',
            )}
          >
            <span className="flex items-center gap-2 truncate">
              {selectedAudience ? (
                <AudienceSharingGlyph
                  audience={selectedAudience}
                  dataTestId="compose-audience-trigger-sharing-glyph"
                />
              ) : (
                <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              {selectedAudience?.name ?? t('compose.selectAudience')}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <div className="flex items-center gap-1.5 border-b border-border p-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={inputRef}
                placeholder={t('compose.searchNameType')}
                aria-label={t('compose.searchAudiences')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 rounded-md border-0 bg-transparent pl-8 text-sm shadow-none focus-visible:ring-0"
                data-testid="compose-audience-search"
              />
            </div>
            {onOpenFilters && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onOpenFilters()
                }}
                data-testid="compose-audience-filters"
                aria-label={t('compose.filterAudiences')}
                className={cn(
                  'flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors',
                  activeFilterCount > 0
                    ? 'border-primary/40 bg-primary/5 text-foreground'
                    : 'border-input text-muted-foreground hover:text-foreground',
                )}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                {t('audiences.filters')}
                {activeFilterCount > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
          </div>

          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('audiences.noneFound')}
              </div>
            ) : (
              filtered.map((audience) => {
                const isSelected = selectedId === audience.id
                return (
                  <button
                    key={audience.id}
                    type="button"
                    onClick={() => {
                      onSelect(audience.id)
                      setOpen(false)
                    }}
                    data-testid={`compose-audience-item-${audience.id}`}
                    className={cn(
                      'relative flex w-full cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm outline-none transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent/50',
                    )}
                  >
                    <AudienceSharingGlyph
                      audience={audience}
                      dataTestId={`compose-audience-sharing-glyph-${audience.id}`}
                    />
                    <span className="flex-1 truncate text-left">{audience.name}</span>
                    {audience.audienceType && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {audience.audienceType}
                      </span>
                    )}
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}
